import { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Table, Form, Button, Spinner, Alert, Pagination, Badge, InputGroup } from 'react-bootstrap'
import client from '../../api/client'

function EmailLogs() {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [filterType, setFilterType] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [searchRecipient, setSearchRecipient] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const perPage = 25

  useEffect(() => {
    fetchLogs()
  }, [page])

  const fetchLogs = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (filterType) params.append('email_type', filterType)
      if (filterStatus) params.append('status', filterStatus)
      if (searchRecipient) params.append('search', searchRecipient)
      if (dateFrom) params.append('date_start', dateFrom)
      if (dateTo) params.append('date_end', dateTo)
      params.append('limit', perPage)
      params.append('offset', (page - 1) * perPage)

      const response = await client.get(`/admin/api/v1/mailing/logs?${params}`)
      setLogs(response.data.logs || response.data || [])
      setTotal(response.data.total || 0)
    } catch (err) {
      console.error('Error fetching email logs:', err)
      setError('Erreur lors du chargement des logs')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    setPage(1)
    fetchLogs()
  }

  const handleReset = () => {
    setFilterType('')
    setFilterStatus('')
    setSearchRecipient('')
    setDateFrom('')
    setDateTo('')
    setPage(1)
    setTimeout(() => fetchLogs(), 100)
  }

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const getStatusBadge = (status) => {
    const variants = {
      sent: 'success',
      failed: 'danger',
      bounced: 'warning',
      pending: 'secondary'
    }
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>
  }

  const getTypeLabel = (type) => {
    const labels = {
      verification: 'Vérification',
      magic_link: 'Magic link',
      password_reset: 'Reset MDP',
      password_change: 'Changement MDP',
      purchase_confirmation: 'Achat',
      renewal_reminder: 'Renouvellement',
      win_back: 'Réactivation',
      newsletter: 'Newsletter',
      ticket_created: 'Ticket créé',
      ticket_reply: 'Réponse',
      ticket_status: 'Statut ticket'
    }
    return labels[type] || type
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const totalPages = Math.ceil(total / perPage)

  const renderPagination = () => {
    if (totalPages <= 1) return null

    const items = []
    const maxVisible = 5
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2))
    let endPage = Math.min(totalPages, startPage + maxVisible - 1)

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1)
    }

    if (page > 1) {
      items.push(
        <Pagination.First key="first" onClick={() => setPage(1)} />,
        <Pagination.Prev key="prev" onClick={() => setPage(page - 1)} />
      )
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item key={i} active={i === page} onClick={() => setPage(i)}>
          {i}
        </Pagination.Item>
      )
    }

    if (page < totalPages) {
      items.push(
        <Pagination.Next key="next" onClick={() => setPage(page + 1)} />,
        <Pagination.Last key="last" onClick={() => setPage(totalPages)} />
      )
    }

    return <Pagination>{items}</Pagination>
  }

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2>📬 Logs Email</h2>
          <p className="text-muted">Historique des emails envoyés</p>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      {/* Filters */}
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Type d'email</Form.Label>
                <Form.Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="">Tous les types</option>
                  <option value="verification">Vérification</option>
                  <option value="magic_link">Magic link</option>
                  <option value="password_reset">Reset MDP</option>
                  <option value="password_change">Changement MDP</option>
                  <option value="purchase_confirmation">Achat</option>
                  <option value="renewal_reminder">Renouvellement</option>
                  <option value="win_back">Réactivation</option>
                  <option value="newsletter">Newsletter</option>
                  <option value="ticket_created">Ticket créé</option>
                  <option value="ticket_reply">Réponse ticket</option>
                  <option value="ticket_status">Statut ticket</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group>
                <Form.Label>Statut</Form.Label>
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">Tous les statuts</option>
                  <option value="sent">Envoyé</option>
                  <option value="failed">Échec</option>
                  <option value="bounced">Rejeté</option>
                  <option value="pending">En attente</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label>Destinataire</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    value={searchRecipient}
                    onChange={(e) => setSearchRecipient(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Email du destinataire"
                  />
                </InputGroup>
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group>
                <Form.Label>Date début</Form.Label>
                <Form.Control
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group>
                <Form.Label>Date fin</Form.Label>
                <Form.Control
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Button variant="primary" onClick={handleSearch} className="me-2">
                🔍 Rechercher
              </Button>
              <Button variant="outline-secondary" onClick={handleReset}>
                Réinitialiser
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Results */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Chargement...</span>
              </Spinner>
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="text-muted">
                  {total} résultat{total > 1 ? 's' : ''} trouvé{total > 1 ? 's' : ''}
                </div>
                {renderPagination()}
              </div>

              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Destinataire</th>
                    <th>Sujet</th>
                    <th>Statut</th>
                    <th>Erreur</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id}>
                      <td>
                        <small>{formatDate(log.created_at)}</small>
                      </td>
                      <td>
                        <Badge bg="info" className="text-white">
                          {getTypeLabel(log.email_type)}
                        </Badge>
                      </td>
                      <td>
                        <code className="small">{log.recipient_email}</code>
                        {log.recipient_user_id && (
                          <div className="small text-muted">
                            User ID: {log.recipient_user_id}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="text-truncate" style={{ maxWidth: '300px' }}>
                          {log.subject || '-'}
                        </div>
                      </td>
                      <td>{getStatusBadge(log.status)}</td>
                      <td>
                        {log.error_message ? (
                          <small className="text-danger text-truncate d-block" style={{ maxWidth: '200px' }}>
                            {log.error_message}
                          </small>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {logs.length === 0 && (
                <div className="text-center py-4 text-muted">
                  Aucun log trouvé
                </div>
              )}

              <div className="d-flex justify-content-end">
                {renderPagination()}
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  )
}

export default EmailLogs
